import React from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, Legend
} from 'recharts';
import Icon from 'components/AppIcon';

const radarData = [
  { subject: 'Climate', asset: 88, benchmark: 72 },
  { subject: 'Biodiversity', asset: 74, benchmark: 68 },
  { subject: 'Water', asset: 81, benchmark: 70 },
  { subject: 'Social', asset: 76, benchmark: 65 },
  { subject: 'Governance', asset: 82, benchmark: 74 },
  { subject: 'Reporting', asset: 79, benchmark: 71 },
];

const ESG_METRICS = [
  { name: 'CO₂ Avoided', category: 'Environmental', value: '48,200', unit: 'tCO₂e/yr', trend: 'up', benchmark: '42,000' },
  { name: 'Renewable Energy Generated', category: 'Environmental', value: '612', unit: 'GWh/yr', trend: 'up', benchmark: '540' },
  { name: 'Water Consumption', category: 'Environmental', value: '1,840', unit: 'm³/yr', trend: 'down', benchmark: '2,100', alert: false },
  { name: 'Waste Diverted from Landfill', category: 'Environmental', value: '94.2', unit: '%', trend: 'up', benchmark: '88%' },
  { name: 'Jobs Created (Direct)', category: 'Social', value: '142', unit: 'FTE', trend: 'stable', benchmark: '120' },
  { name: 'Local Procurement', category: 'Social', value: '67', unit: '%', trend: 'up', benchmark: '55%' },
  { name: 'Community Investment', category: 'Social', value: '£0.8m', unit: 'p.a.', trend: 'stable', benchmark: '£0.6m' },
  { name: 'Board Diversity', category: 'Governance', value: '42', unit: '% female', trend: 'up', benchmark: '35%' },
  { name: 'Independent Directors', category: 'Governance', value: '67', unit: '%', trend: 'stable', benchmark: '60%', alert: false },
  { name: 'ESG Reporting Standard', category: 'Governance', value: 'GRI + TCFD', unit: '', trend: 'stable', benchmark: 'GRI' },
];

const BELOW_THRESHOLD = ESG_METRICS?.filter(m => m?.alert === true);

const SubScore = ({ label, score, color }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <span className="font-caption text-foreground text-sm font-medium">{label}</span>
      <span className="font-heading font-semibold text-foreground" style={{ fontSize: '1.125rem' }}>{score}</span>
    </div>
    <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, background: color }} />
    </div>
  </div>
);

export default function ESGTab({ asset }) {
  const overallScore = asset?.esg || 82;

  return (
    <div className="space-y-6">
      {BELOW_THRESHOLD?.length > 0 && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-red-200 bg-red-50">
          <Icon name="AlertCircle" size={16} color="#DC2626" className="flex-shrink-0 mt-0.5" />
          <p className="font-caption text-red-800 text-sm">
            <strong>{BELOW_THRESHOLD?.length} metric{BELOW_THRESHOLD?.length > 1 ? 's' : ''} below threshold.</strong> Review required: {BELOW_THRESHOLD?.map(m => m?.name)?.join(', ')}.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overall Score + Sub-scores */}
        <div className="card space-y-5" style={{ padding: '20px 24px' }}>
          <div>
            <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: '0.9375rem' }}>ESG Score Overview</h3>
            <p className="font-caption text-muted-foreground text-xs mt-0.5">Sample data · SFDR Article 9 aligned</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex-shrink-0 flex flex-col items-center justify-center w-24 h-24 rounded-full border-4 border-green-500 bg-green-50">
              <span className="font-heading font-bold text-green-700" style={{ fontSize: '1.75rem', lineHeight: 1 }}>{overallScore}</span>
              <span className="font-caption text-green-600 text-xs">/100</span>
            </div>
            <div className="flex-1 space-y-3">
              <SubScore label="Environmental" score={88} color="#059669" />
              <SubScore label="Social" score={76} color="#2563EB" />
              <SubScore label="Governance" score={82} color="#7C3AED" />
            </div>
          </div>
        </div>

        {/* Radar Chart */}
        <div className="card" style={{ padding: '20px 24px' }}>
          <h3 className="font-heading font-semibold text-foreground mb-1" style={{ fontSize: '0.9375rem' }}>ESG Profile vs Sector Benchmark</h3>
          <p className="font-caption text-muted-foreground text-xs mb-2">Sample data</p>
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="var(--color-border)" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: 'var(--color-muted-foreground)' }} />
              <Radar name="This Asset" dataKey="asset" stroke="#059669" fill="#059669" fillOpacity={0.18} strokeWidth={2} />
              <Radar name="Sector Benchmark" dataKey="benchmark" stroke="#2563EB" fill="#2563EB" fillOpacity={0.08} strokeWidth={1.5} strokeDasharray="4 2" />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)' }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Metrics Table */}
      <div className="card overflow-hidden" style={{ padding: 0 }}>
        <div className="px-5 py-3 border-b border-border bg-muted/30">
          <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: '0.875rem' }}>ESG Metrics Detail</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {['Metric', 'Category', 'Value', 'Unit', 'Trend', 'Benchmark']?.map(h => (
                  <th key={h} className="px-4 py-2.5 text-left font-caption text-muted-foreground" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ESG_METRICS?.map((m, i) => (
                <tr key={m?.name} className={`border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-muted/5'}`}>
                  <td className="px-4 py-2.5 font-medium text-foreground">{m?.name}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      m?.category === 'Environmental' ? 'bg-green-100 text-green-700' :
                      m?.category === 'Social'? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>{m?.category}</span>
                  </td>
                  <td className="px-4 py-2.5 font-mono font-semibold text-foreground">{m?.value}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{m?.unit}</td>
                  <td className="px-4 py-2.5">
                    <Icon
                      name={m?.trend === 'up' ? 'TrendingUp' : m?.trend === 'down' ? 'TrendingDown' : 'Minus'}
                      size={15}
                      color={m?.trend === 'up' ? '#059669' : m?.trend === 'down' ? '#DC2626' : 'var(--color-muted-foreground)'}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{m?.benchmark}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
